import { markRaw, ref } from 'vue';

const modals = ref([]);
let counter = 0;

export const useModal = () => {
  /**
   * Show a modal dialog
   * @param {Object} config - Modal configuration
   * @param {string} config.title - Modal title
   * @param {string} config.message - Modal message
   * @param {Object|Function} config.content - Custom content component (Vue component or render function)
   * @param {Object} config.contentProps - Props to pass to the custom content component
   * @param {boolean} config.showCloseButton - Whether to show close button (default: true)
   * @param {string} config.size - Modal size (sm, md, lg, xl) (default: md)
   * @param {number} config.zIndex - Custom z-index for the modal (default: 1050)
   * @param {boolean} config.backdropClickClose - Allow closing on backdrop click (default: false)
   * @param {string} config.icon - Optional icon class (Font Awesome)
   * @param {boolean} config.spinner - Whether to show a spinner (default: false)
   * @param {string} config.spinnerVariant - Spinner variant (default: primary)
   * @param {string} config.iconVariant - Color variant for the icon (default: primary)
   * @param {boolean} config.blurBackdrop - Whether to apply blur to the backdrop (default: true)
   * @param {Array} config.actions - Array of action buttons for the modal footer
   * @param {string} config.transitionName - Custom transition name for modal (default: modal-fade)
   * @returns {number} - Modal ID
   */
  const showModal = config => {
    const id = counter++;
    const modal = {
      id,
      title: config.title || '',
      message: config.message || '',
      content: config.content ? markRaw(config.content) : null,
      contentProps: config.contentProps || {},
      showCloseButton:
        config.showCloseButton !== undefined ? config.showCloseButton : true,
      size: config.size || 'md',
      zIndex: config.zIndex || 1050,
      backdropClickClose:
        config.backdropClickClose !== undefined
          ? config.backdropClickClose
          : false,
      icon: config.icon || null,
      spinner: config.spinner !== undefined ? config.spinner : false,
      spinnerVariant: config.spinnerVariant || 'primary',
      iconVariant: config.iconVariant || 'primary',
      blurBackdrop:
        config.blurBackdrop !== undefined ? config.blurBackdrop : true,
      actions: config.actions || [],
      visible: true,
      transitionName: config.transitionName || 'modal-fade',
    };

    modals.value.push(modal);
    return id;
  };

  /**
   * Hide a specific modal by ID
   * @param {number} id - Modal ID to hide
   */
  const hideModal = id => {
    const index = modals.value.findIndex(modal => modal.id === id);
    if (index !== -1) {
      modals.value.splice(index, 1);
    }
  };

  /**
   * Hide all modals
   */
  const hideAllModals = () => {
    modals.value = [];
  };

  /**
   * Update an existing modal with new options
   * @param {number} id - Modal ID to update
   * @param {Object} newOptions - New options to apply
   * @returns {boolean} - Whether the modal was found and updated
   */
  const updateModal = (id, newOptions) => {
    const index = modals.value.findIndex(modal => modal.id === id);
    if (index !== -1) {
      modals.value[index] = {
        ...modals.value[index],
        ...newOptions,
        // If updating content, ensure it's wrapped with markRaw
        content: newOptions.content
          ? markRaw(newOptions.content)
          : modals.value[index].content,
      };
      return true;
    }
    return false;
  };

  /**
   * Switch the content of a modal (keeping the same ID)
   * Useful for multi-step flows where you want to keep the same modal open
   * @param {number} id - Modal ID to update
   * @param {Object} newConfig - New configuration
   * @returns {boolean} - Whether the switch was successful
   */
  const switchModal = (id, newConfig) => {
    return updateModal(id, newConfig);
  };

  /**
   * Show a loading modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const loading = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: false,
      spinner: true,
      spinnerVariant: 'primary',
      size: 'sm',
      backdropClickClose: false,
      ...options,
    });
  };

  /**
   * Show a process modal (for switching/changing states)
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const process = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton:
        options.showCloseButton !== undefined ? options.showCloseButton : false,
      icon: options.icon || 'fas fa-cog fa-spin',
      iconVariant: options.iconVariant || 'primary',
      spinner: options.spinner !== undefined ? options.spinner : true,
      spinnerVariant: options.spinnerVariant || 'primary',
      size: options.size || 'sm',
      backdropClickClose: false,
      blurBackdrop:
        options.blurBackdrop !== undefined ? options.blurBackdrop : true,
      ...options,
    });
  };

  /**
   * Show a success modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const success = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: true,
      icon: options.icon || 'fas fa-check-circle',
      iconVariant: 'success',
      size: options.size || 'sm',
      backdropClickClose:
        options.backdropClickClose !== undefined
          ? options.backdropClickClose
          : true,
      actions: options.actions || [
        {
          label: options.buttonLabel || 'OK',
          variant: 'success',
          handler: (_, modalId) => hideModal(modalId),
        },
      ],
      ...options,
    });
  };

  /**
   * Show an error modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const error = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: true,
      icon: options.icon || 'fas fa-exclamation-circle',
      iconVariant: 'danger',
      size: options.size || 'sm',
      backdropClickClose:
        options.backdropClickClose !== undefined
          ? options.backdropClickClose
          : true,
      actions: options.actions || [
        {
          label: options.buttonLabel || 'OK',
          variant: 'danger',
          handler: (_, modalId) => hideModal(modalId),
        },
      ],
      ...options,
    });
  };

  /**
   * Show a warning modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const warning = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: true,
      icon: options.icon || 'fas fa-exclamation-triangle',
      iconVariant: 'warning',
      size: options.size || 'sm',
      backdropClickClose:
        options.backdropClickClose !== undefined
          ? options.backdropClickClose
          : true,
      actions: options.actions || [
        {
          label: options.buttonLabel || 'OK',
          variant: 'warning',
          handler: (_, modalId) => hideModal(modalId),
        },
      ],
      ...options,
    });
  };

  /**
   * Show an info modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const info = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: true,
      icon: options.icon || 'fas fa-info-circle',
      iconVariant: 'info',
      size: options.size || 'sm',
      backdropClickClose:
        options.backdropClickClose !== undefined
          ? options.backdropClickClose
          : true,
      actions: options.actions || [
        {
          label: options.buttonLabel || 'OK',
          variant: 'info',
          handler: (_, modalId) => hideModal(modalId),
        },
      ],
      ...options,
    });
  };

  /**
   * Show an alert modal
   * @param {string} title - Modal title
   * @param {string} message - Modal message
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const alert = (title, message, options = {}) => {
    return showModal({
      title,
      message,
      showCloseButton: false,
      icon: options.icon || 'fas fa-bell',
      iconVariant: options.iconVariant || 'primary',
      size: options.size || 'sm',
      backdropClickClose: false,
      actions: options.actions || [
        {
          label: options.buttonLabel || 'OK',
          variant: options.buttonVariant || 'primary',
          handler: (_, modalId) => hideModal(modalId),
        },
      ],
      ...options,
    });
  };

  /**
   * @param {string} title
   * @param {string} message
   * @param {{ onConfirm?: () => void | Promise<void>, onCancel?: () => void, confirmLabel?: string, cancelLabel?: string, confirmVariant?: string, showCloseButton?: boolean, icon?: string, iconVariant?: string, size?: string, actions?: any[] }} [options]
   * @returns {number} - Modal ID
   */
  const confirm = (title, message, options = {}) => {
    const defaultActions = [
      {
        label: options.cancelLabel || 'Cancel',
        variant: 'outline-secondary',
        handler: () => {
          if (options.onCancel) {
            options.onCancel();
          }
          return hideModal(modalId);
        },
      },
      {
        label: options.confirmLabel || 'Confirm',
        variant: options.confirmVariant || 'primary',
        handler: () => {
          if (options.onConfirm) {
            options.onConfirm();
          }
          return hideModal(modalId);
        },
      },
    ];

    const modalId = showModal({
      title,
      message,
      showCloseButton:
        options.showCloseButton !== undefined ? options.showCloseButton : true,
      icon: options.icon || 'fas fa-question-circle',
      iconVariant: options.iconVariant || 'primary',
      size: options.size || 'sm',
      backdropClickClose:
        options.backdropClickClose !== undefined
          ? options.backdropClickClose
          : false,
      actions: options.actions || defaultActions,
      ...options,
    });

    return modalId;
  };

  /**
   * Show a company switching modal
   * @param {string} companyName - Name of the company being switched to
   * @param {Object} options - Additional options
   * @returns {number} - Modal ID
   */
  const companySwitching = (companyName, options = {}) => {
    const { $i18n } = useNuxtApp();
    return showModal({
      title: options.title || $i18n.t('client.company_switch.changing_title'),
      message:
        options.message ||
        $i18n.t('client.company_switch.changing_message', {
          company: companyName,
        }),
      spinner: true,
      spinnerVariant: 'primary',
      showCloseButton: false,
      backdropClickClose: false,
      size: 'sm',
      icon: 'fas fa-exchange-alt',
      iconVariant: 'primary',
      blurBackdrop: true,
      transitionName: 'modal-slide',
      ...options,
    });
  };

  return {
    modals,
    showModal,
    hideModal,
    hideAllModals,
    updateModal,
    switchModal,
    loading,
    process,
    success,
    error,
    warning,
    info,
    alert,
    confirm,
    companySwitching,
  };
};
